import { useState } from "react"

import {
  MasterclassSwatch,
  type MasterclassSwatchSize,
} from "@/components/learn/masterclass-swatch"
import { MASTERCLASS_COVER_ASPECT_CLASS } from "@/lib/masterclass-cover"

/**
 * OKS-649 — Resolved masterclass cover renderer, shared across every
 * display surface (brand card banner, creator Learn list card banner,
 * creator Learn detail hero). Renders the attached cover `<img>` when a
 * resolved URL is present, else falls back to the deterministic
 * `MasterclassSwatch` (`image || swatch`, UX Spec §2).
 *
 * The server resolves `coverImageUrl` to the RIGHT variant per surface
 * (`:card` banner on the brand card, creator Learn list card, and creator
 * hero — all 3:2 since OKS-836) via `Masterclass#cover_image_display_url`,
 * so this component just picks img-vs-swatch and applies the size
 * footprint. The `"thumb"` size remains for compact non-banner surfaces
 * (e.g. the completed-masterclasses item).
 *
 * Error resilience (UX Spec §3): a broken/purged blob URL that fails to
 * load swaps back to the swatch via `onError` — a display surface never
 * shows a broken-image glyph.
 *
 * Testid contract (mirrors the shipped mission-cover pattern): the base
 * `data-testid` is suffixed per branch — `${testId}-image` on the `<img>`,
 * `${testId}-fallback` on the swatch — so specs can assert precisely
 * which branch rendered (and that a cover-less row shows NO `<img>`).
 */
export interface MasterclassCoverProps {
  /** Masterclass id — drives the swatch fallback palette. */
  id: number
  /** Resolved cover URL (right variant chosen server-side) or null. */
  coverImageUrl: string | null
  /** Masterclass title — used as the `<img>` alt text. */
  title: string | null
  /** `"card"` (full-width 3:2 banner) or `"thumb"` (56px square) footprint. */
  size: MasterclassSwatchSize
  /** Optional override classes on the wrapper img/swatch. */
  className?: string
  "data-testid"?: string
}

export function MasterclassCover({
  id,
  coverImageUrl,
  title,
  size,
  className,
  "data-testid": testId,
}: MasterclassCoverProps) {
  const [errored, setErrored] = useState(false)

  const showImage = Boolean(coverImageUrl) && !errored

  if (showImage) {
    // OKS-768 — the `card` banner is locked to the masterclass cover ratio
    // (3:2) so the framed image is what shows (framed==shown). The `thumb`
    // stays a 56px square — a center-square derivation of the banner crop,
    // deliberately excluded from framed==shown (see `lib/masterclass-cover`).
    const imgClass =
      size === "card"
        ? `${MASTERCLASS_COVER_ASPECT_CLASS} w-full object-cover`
        : "h-14 w-14 shrink-0 rounded-md object-cover"

    return (
      <img
        src={coverImageUrl!}
        alt={title ?? ""}
        onError={() => setErrored(true)}
        data-testid={testId ? `${testId}-image` : undefined}
        className={className ? `${imgClass} ${className}` : imgClass}
      />
    )
  }

  return (
    <MasterclassSwatch
      id={id}
      size={size}
      className={className}
      data-testid={testId ? `${testId}-fallback` : undefined}
    />
  )
}

export default MasterclassCover
